import { CdkDrag, CdkDragHandle } from "@angular/cdk/drag-drop";
import { AsyncPipe, CommonModule, DatePipe } from "@angular/common";
import { Component, OnDestroy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { AuthService } from "app/core/auth/service/auth.service";
import { LanguageService } from "app/core/services/language.service";
import { LoggerConfigService } from "app/core/services/logger-config.service";
import { THEMES } from "app/core/theme/theme-data";
import { Theme } from "app/core/theme/theme.interface";
import { ThemeService } from "app/core/theme/theme.service";
import {
  Deployment,
  TestToolService,
} from "app/shared/components/test-tool/test-tool.service";
import { StorageKey } from "app/shared/enums/storage-key.enum";
import { CookieService } from "ngx-cookie-service";
import { NGXLogger } from "ngx-logger";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// See: https://confluence.nfon.net/x/Kw04L
@Component({
  selector: "test-tool",
  templateUrl: "./test-tool.component.html",
  styleUrls: ["./test-tool.component.scss"],
  imports: [
    FormsModule,
    AsyncPipe,
    CdkDrag,
    CdkDragHandle,
    DatePipe,
    CommonModule,
    TranslateModule,
  ],
  standalone: true,
})
export class TestToolComponent implements OnDestroy {
  public readonly selectMaxLength: number = 20;

  public isHidden: boolean;

  public deployments$: Observable<Deployment[]> =
    this.testToolService.deployments$;
  public selectDeployment: Deployment | undefined;
  public latestDeployments$: Observable<Deployment[]> =
    this.testToolService.latestDeployments$;

  public themes: Theme[] = THEMES;
  public selectTheme: Theme;

  // Index in the array represents the level.
  public logLevels: string[] = LoggerConfigService.logLevels.map(
    (logLevel: string, index: number) => index + " " + logLevel,
  );
  public selectLogLevel: number;

  public languages$ = this.languageService.availableLanguages$;
  public selectLanguage: string | undefined;
  public isLanguageRotationActive$ =
    this.languageService.isLanguageRotationActive$;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private testToolService: TestToolService,
    private themeService: ThemeService,
    private languageService: LanguageService,
    private logger: NGXLogger,
    private loggerConfigService: LoggerConfigService,
    private cookieService: CookieService,
    private authService: AuthService,
  ) {
    this.isHidden = localStorage.getItem(StorageKey.TestToolHidden) === "true";

    this.testToolService.currentDeployment$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((deployment) => {
        this.selectDeployment = deployment;
      });

    this.themeService.activeTheme$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((theme) => {
        this.selectTheme = theme;
      });

    this.languageService.currentLanguage$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((language) => {
        this.selectLanguage = language.value;
      });

    this.selectLogLevel = this.logger.getConfigSnapshot().level;
  }

  public toggleTestTool(): void {
    this.isHidden = !this.isHidden;
    localStorage.setItem(StorageKey.TestToolHidden, this.isHidden.toString());
  }

  public applyDeployment(): void {
    if (this.selectDeployment)
      this.testToolService.switchToDeployment(this.selectDeployment);
  }

  public applyTheme(): void {
    this.clearSiteData();
    this.themeService.changeTheme(this.selectTheme.value);
    // Automatically reload to apply fully.
    window.location.reload();
  }

  public applyLogLevel(): void {
    this.loggerConfigService.setLogLevel(this.selectLogLevel);
  }

  public applyLanguage(): void {
    if (this.selectLanguage)
      this.languageService.updateLanguage(this.selectLanguage);
  }

  public toggleLanguageRotation(): void {
    this.languageService.toggleLanguageRotation();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public clearSiteDataAndReload(): void {
    this.clearSiteData();
    // Automatically reload to apply fully.
    window.location.reload();
  }

  public refreshToken(): void {
    this.authService.updateToken();
  }

  private clearSiteData(): void {
    sessionStorage.clear();
    localStorage.clear();
    this.cookieService.deleteAll();
  }
}
